import { AuthTokens, User } from '@shopify-multiseller/graphql/partner/types';
import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface IAuthenticatedUserContext {
  user: User | null;
  tokens: AuthTokens | null;
  update: (newUser: User, newTokens: AuthTokens) => void;
  updateUser: (newUser: User) => void;
  updateTokens: (newTokens: AuthTokens) => void;
  destroy: () => void;
}

const AuthenticatedUserContext = createContext<IAuthenticatedUserContext | undefined>(undefined);

const AuthenticatedUserProvider = ({ children }: { children: ReactNode }) => {
  const localStorageUser = localStorage.getItem('user');
  const sessionStorageTokens = sessionStorage.getItem('tokens');

  const [authUser, setAuthUser] = useState<IAuthenticatedUserContext>({
    user: localStorageUser != null ? (JSON.parse(localStorageUser) as User) : null,
    tokens: sessionStorageTokens != null ? (JSON.parse(sessionStorageTokens) as AuthTokens) : null,
    update: (newUser: User, newTokens: AuthTokens) => {
      // Save to storage SYNCHRONOUSLY to avoid race conditions
      localStorage.setItem('user', JSON.stringify(newUser));
      sessionStorage.setItem('tokens', JSON.stringify(newTokens));
      setAuthUser({ ...authUser, user: newUser, tokens: newTokens });
    },
    updateUser: (newUser: User) => {
      localStorage.setItem('user', JSON.stringify(newUser));
      setAuthUser((prev) => ({ ...prev, user: newUser }));
    },
    updateTokens: (newTokens: AuthTokens) => {
      sessionStorage.setItem('tokens', JSON.stringify(newTokens));
      setAuthUser((prev) => ({ ...prev, tokens: newTokens }));
    },
    destroy: () => {
        localStorage.removeItem('user');
        sessionStorage.removeItem('tokens');
        setAuthUser(authUser => ({ ...authUser, user: null, tokens: null }));
    },
  });

  useEffect(() => {
    if (authUser.user !== null) {
      localStorage.setItem('user', JSON.stringify(authUser.user));
    } else {
      localStorage.removeItem('user');
    }

    if (authUser.tokens !== null) {
      sessionStorage.setItem('tokens', JSON.stringify(authUser.tokens));
    } else {
      sessionStorage.removeItem('tokens');
    }
  }, [authUser]);

  return (
    <AuthenticatedUserContext.Provider value={authUser}>
      {children}
    </AuthenticatedUserContext.Provider>
  );
};

const useAuthenticatedUser = (): IAuthenticatedUserContext => {
  const context = useContext(AuthenticatedUserContext);
  if (!context) {
    throw new Error('useAuthenticatedUserContext must be used within a AuthenticatedUserProvider');
  }
  return context;
};

export { AuthenticatedUserProvider, useAuthenticatedUser };
